/**
 * Shared post-authentication session plumbing.
 *
 * After ANY flow that yields a full auth response + new tokens (login,
 * /auth/selectOrganization, /auth/selectOrganizationByStudent) the same
 * side effects must run so the app reflects the new org-scoped session:
 * - ETL setup state (staff only — Students/Guardians/ClassVeew Admins skip the fetch)
 * - non-sensitive user metadata in localStorage (userId, orgLogo, userImage, currency)
 * - privileges + review decoded from the new accessToken (sessionStorage +
 *   `privilegesUpdated` window event, which the sidebar listens to)
 * - user role persisted for role-based routing
 *
 * Centralized here so the login page, the organization-selection page and the
 * guardian sidebar student switcher stay in sync.
 */

import { accessPrivilegeReview, setUserRole } from './jwt_expiry';
import { tokenManager } from './tokenManager';
import { TOKEN_TYPES, USER_ROLES } from '../constant/auth.constant';
import { fetchETLStatus } from '../redux/action';
import { setSetupComplete } from '../redux/slices/etl.slice';
import type { AppDispatch } from '../redux/store';
import type { ILoginResponse } from '../interface/auth.interface';

/**
 * Finalize an authenticated session from a full auth response body.
 * Call AFTER the new accessToken/refreshToken have been stored (the auth
 * slice reducers store them via tokenManager).
 *
 * @param dispatch - Typed app dispatch
 * @param response - Full auth response body (complete-login shape)
 */
export const finalizeAuthSession = async (
  dispatch: AppDispatch,
  response: ILoginResponse | undefined,
): Promise<void> => {
  // ETL setup only applies to organization staff (Super Admins). ClassVeew
  // Admins, Students and Guardians (empty/role-scoped privileges, no ETL
  // setup) skip the fetch so a failing call never blocks their navigation.
  if (response?.role === USER_ROLES.CLASSVEEW_ADMIN || response?.role === USER_ROLES.PARENT) {
    dispatch(setSetupComplete(true));
  } else if (response?.role !== USER_ROLES.STUDENT) {
    await dispatch(fetchETLStatus()).unwrap();
  }

  // Store additional non-sensitive user metadata in localStorage
  if (response?.profile) {
    localStorage.setItem('userId', response.profile.userId);
    localStorage.setItem('orgLogo', response.profile.orgLogo || '');
    localStorage.setItem('userImage', response.profile.upload || '');
    localStorage.setItem('currency', response.profile.orgcurrency || '');
    // Sidebar listens for this to refresh the displayed organization logo
    window.dispatchEvent(new Event('orgLogoUpdated'));
  }

  // Decode and store privileges/review from the (new) accessToken; fires the
  // `privilegesUpdated` window event so privilege-driven UI re-renders.
  const accessToken = tokenManager.getToken(TOKEN_TYPES.ACCESS_TOKEN);
  if (accessToken) {
    accessPrivilegeReview(accessToken);
  }

  // Persist the user's role (same expiry as privileges) for role-based routing
  if (response?.role) {
    setUserRole(response.role);
  }
};
